/**
 * VISTOERA_PLATFORM_BRANDING_SETTINGS_DYNAMIC_FAVICON_P0_V1 — canonical
 * platform favicon bootstrap for Login / Tenant / Canvas / Platform Admin.
 */

import { getStoredPreviewAccessKey, PREVIEW_ACCESS_HEADER } from '../api/client'
import { resolveMediaUrlAgainstOrigin } from './mediaUrlCore'

export const BUNDLED_FAVICON_32 = '/brand/favicon-32x32.png'
export const BUNDLED_FAVICON_16 = '/brand/favicon-16x16.png'
export const BUNDLED_APPLE_TOUCH = '/brand/apple-touch-icon.png'

export type PublicPlatformBranding = {
  platform_name: string
  favicon: {
    asset_id: string | null
    url: string | null
    updated_at: string | null
  }
}

let _bootstrapPromise: Promise<PublicPlatformBranding | null> | null = null

function apiOrigin(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim()
  if (raw) {
    const trimmed = raw.replace(/\/+$/, '')
    return trimmed.endsWith('/api') ? trimmed.slice(0, -4) : trimmed
  }
  return window.location.origin
}

function resolveFaviconHref(pathOrUrl: string | null | undefined): string | null {
  if (!pathOrUrl) return null
  const resolved = resolveMediaUrlAgainstOrigin(pathOrUrl, apiOrigin())
  return resolved || pathOrUrl
}

export function applyPlatformFavicon(href: string, contentType = 'image/png'): void {
  if (!href || typeof document === 'undefined') return
  const head = document.head
  const links = head.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')
  links.forEach((link) => link.remove())

  const icon = document.createElement('link')
  icon.rel = 'icon'
  icon.type = contentType
  icon.href = href
  head.appendChild(icon)

  const icon16 = document.createElement('link')
  icon16.rel = 'icon'
  icon16.type = contentType
  icon16.sizes = '16x16'
  icon16.href = href
  head.appendChild(icon16)

  const icon32 = document.createElement('link')
  icon32.rel = 'icon'
  icon32.type = contentType
  icon32.sizes = '32x32'
  icon32.href = href
  head.appendChild(icon32)

  const apple = document.createElement('link')
  apple.rel = 'apple-touch-icon'
  apple.href = href
  head.appendChild(apple)
}

export function applyBundledDefaultFavicon(): void {
  applyPlatformFavicon(BUNDLED_FAVICON_32, 'image/png')
}

export async function fetchPublicPlatformBranding(): Promise<PublicPlatformBranding | null> {
  if (_bootstrapPromise) return _bootstrapPromise
  _bootstrapPromise = (async () => {
    try {
      const headers: Record<string, string> = { Accept: 'application/json' }
      const previewKey = getStoredPreviewAccessKey()
      if (previewKey) headers[PREVIEW_ACCESS_HEADER] = previewKey
      const url = `${apiOrigin()}/api/public/platform-branding`
      const res = await fetch(url, { headers, credentials: 'omit' })
      if (!res.ok) return null
      return (await res.json()) as PublicPlatformBranding
    } catch {
      return null
    }
  })()
  return _bootstrapPromise
}

export async function bootstrapPlatformFavicon(): Promise<void> {
  applyBundledDefaultFavicon()
  const branding = await fetchPublicPlatformBranding()
  const custom = resolveFaviconHref(branding?.favicon?.url)
  if (custom) {
    applyPlatformFavicon(custom)
  }
}

/** After admin upload — immediate tab update without full reload. */
export function applyUploadedPlatformFavicon(url: string, contentType?: string): void {
  const href = resolveFaviconHref(url) || url
  applyPlatformFavicon(href, contentType || 'image/png')
  _bootstrapPromise = null
}

export function resolvePlatformBrandingAssetUrl(pathOrUrl: string | null | undefined): string | null {
  return resolveFaviconHref(pathOrUrl)
}
